import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';
import DetalleCxPModal from './DetalleCxPModal';

const ReporteCxP = ({ month, year }) => {
    const { userData } = useAuth();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [totals, setTotals] = useState({
        current: 0,
        d30: 0,
        d60: 0,
        d90: 0,
        d90plus: 0,
        total: 0
    });

    const fetchData = async () => {
        if (!userData?.condominioId) return;
        setLoading(true);
        try {
            // Report Date = End of selected month
            const reportDate = moment().year(year).month(month).endOf('month');

            // Fetch ALL Expenses (Gastos)
            // We typically care about "Provisions" (unpaid bills).
            // Existing expenses from Bank Feed are "Paid" by definition (Cash Basis).
            // So we focus on docs where 'provisionado' is true or 'estatus' is 'pendiente'.

            const q = query(
                collection(db, "gastos"),
                where("condominioId", "==", userData.condominioId),
                where("tipo", "==", "egreso")
                // We fetch all egresos to be safe, then filter in memory for 'pendiente' logic
            );

            const snap = await getDocs(q);

            const agingMap = {};

            snap.forEach(doc => {
                const gasto = doc.data();

                // Filter 1: Must be created before or on Report Date
                const createdAt = gasto.fecha?.toDate ? moment(gasto.fecha.toDate()) : moment();
                if (createdAt.isAfter(reportDate, 'day')) return;

                // Filter 2: Must be "Open" (Unpaid)
                // If it has 'movimientoBancarioId', it was immediate cash payment -> Ignored for AP Aging
                // If it is 'provisionado' === true:
                //    Check if 'estatus' is 'pagado' AND 'fechaPago' <= reportDate. (Future feature)
                //    For now, if 'estatus' == 'pendiente', it is open.

                let isOpen = false;
                if (gasto.provisionado || gasto.estatus === 'pendiente') {
                    isOpen = true;
                    // Future: If status is 'pagado', check date. 
                    if (gasto.estatus === 'pagado') {
                        const paidAt = gasto.fechaPago?.toDate ? moment(gasto.fechaPago.toDate()) : null;
                        if (paidAt && paidAt.isAfter(reportDate)) {
                            isOpen = true; // Was open back then
                        } else {
                            isOpen = false; // Paid before report date
                        }
                    }
                }

                if (!isOpen) return;

                const providerName = gasto.proveedor || 'Proveedor Desconocido';
                const amount = parseFloat(gasto.monto || 0);

                // Group by Provider
                if (!agingMap[providerName]) {
                    agingMap[providerName] = {
                        provider: providerName,
                        current: 0,
                        d30: 0,
                        d60: 0,
                        d90: 0,
                        d90plus: 0,
                        total: 0
                    };
                }

                // Aging Logic (Month-based)
                const dueDate = gasto.fechaVencimiento?.toDate ? moment(gasto.fechaVencimiento.toDate()) : moment();
                const dueMonth = moment(dueDate).startOf('month');
                const reportMonthStart = moment(reportDate).startOf('month');

                const monthsOverdue = reportMonthStart.diff(dueMonth, 'months');

                if (monthsOverdue <= 0) {
                    agingMap[providerName].current += amount;
                } else if (monthsOverdue === 1) {
                    agingMap[providerName].d30 += amount;
                } else if (monthsOverdue === 2) {
                    agingMap[providerName].d60 += amount;
                } else if (monthsOverdue === 3) {
                    agingMap[providerName].d90 += amount;
                } else {
                    agingMap[providerName].d90plus += amount;
                }

                agingMap[providerName].total += amount;
            });

            // Convert to Array & Sort
            const result = Object.values(agingMap).sort((a, b) => b.total - a.total);
            setReportData(result);

            // Calculate Totals
            const newTotals = result.reduce((acc, row) => ({
                current: acc.current + row.current,
                d30: acc.d30 + row.d30,
                d60: acc.d60 + row.d60,
                d90: acc.d90 + row.d90,
                d90plus: acc.d90plus + row.d90plus,
                total: acc.total + row.total
            }), { current: 0, d30: 0, d60: 0, d90: 0, d90plus: 0, total: 0 });

            setTotals(newTotals);

        } catch (error) {
            console.error("Error generating AP Report:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, month, year]);

    const getExportData = () => {
        return reportData.map(row => ({
            'Proveedor': row.provider,
            'Al Corriente': row.current,
            '1-30 Días': row.d30,
            '31-60 Días': row.d60,
            '61-90 Días': row.d90,
            '+90 Días': row.d90plus,
            'Total': row.total
        }));
    };

    const handleExportCSV = () => {
        exportToCSV(getExportData(), `Reporte_CxP_${moment().month(month).format('MMMM')}_${year}`);
    };

    const handleExportExcel = () => {
        exportToExcel(getExportData(), `Reporte_CxP_${moment().month(month).format('MMMM')}_${year}`, 'CxP', userData?.condominioNombre);
    };

    const handleExportPDF = () => {
        const columns = ['Proveedor', 'Al Corriente', '1-30 Días', '31-60 Días', '61-90 Días', '+90 Días', 'Total'];
        const data = reportData.map(row => [
            row.provider,
            `$${new Intl.NumberFormat('es-MX').format(row.current)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d30)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d60)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d90)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d90plus)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.total)}`
        ]);

        // Add totals
        data.push([
            'TOTALES',
            `$${new Intl.NumberFormat('es-MX').format(totals.current)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d30)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d60)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d90)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d90plus)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.total)}`
        ]);

        exportToPDF(
            `Reporte de Cuentas por Pagar (CxP) - ${moment().month(month).format('MMMM')} ${year}`,
            columns,
            data,
            `Reporte_CxP_${year}_${month + 1}`,
            userData?.condominioNombre
        );
    };

    if (loading) return <div className="loading-state">Calculando cuentas por pagar...</div>;

    return (
        <div className="report-container fade-in">
            <div className="report-header-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div className="kpi-card-mini" style={{ background: '#f8f9fa', padding: '10px 20px', borderRadius: '8px', borderLeft: '4px solid #dc3545' }}>
                    <span style={{ fontSize: '0.9em', color: '#666' }}>Total Por Pagar</span>
                    <h3 style={{ margin: 0, color: '#dc3545' }}>
                        ${new Intl.NumberFormat('es-MX').format(totals.total)}
                    </h3>
                </div>

                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>

            <div className="table-responsive">
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th>Proveedor</th>
                            <th style={{ textAlign: 'right' }}>Al Corriente</th>
                            <th style={{ textAlign: 'right', color: '#f59e0b' }}>1-30 Días</th>
                            <th style={{ textAlign: 'right', color: '#f97316' }}>31-60 Días</th>
                            <th style={{ textAlign: 'right', color: '#ef4444' }}>61-90 Días</th>
                            <th style={{ textAlign: 'right', color: '#b91c1c' }}>+90 Días</th>
                            <th style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.length === 0 ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No hay cuentas por pagar pendientes.</td></tr>
                        ) : reportData.map((row, idx) => (
                            <tr
                                key={idx}
                                onClick={() => setSelectedProvider(row.provider)}
                                style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                                className="clickable-row"
                            >
                                <td style={{ fontWeight: '500', color: '#2563eb' }}>{row.provider}</td>
                                <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(row.current)}</td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d30 > 0 ? '#fffbeb' : 'transparent' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d30)}
                                </td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d60 > 0 ? '#fff7ed' : 'transparent' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d60)}
                                </td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d90 > 0 ? '#fef2f2' : 'transparent' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d90)}
                                </td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d90plus > 0 ? '#fef2f2' : 'transparent', fontWeight: row.d90plus > 0 ? 'bold' : 'normal', color: row.d90plus > 0 ? '#b91c1c' : 'inherit' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d90plus)}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.total)}
                                </td>
                            </tr>
                        ))}
                        {/* Totals Row */}
                        <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 'bold' }}>
                            <td style={{ textAlign: 'right' }}>Totales:</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.current)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d30)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d60)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d90)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d90plus)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '20px', fontSize: '0.85em', color: '#666' }}>
                <p><strong>Nota:</strong> Este reporte incluye gastos provisionados (facturas recibidas) aún no pagados. Haz clic en un proveedor para ver detalles.</p>
            </div>

            {selectedProvider && (
                <DetalleCxPModal
                    provider={selectedProvider}
                    month={month}
                    year={year}
                    onClose={() => setSelectedProvider(null)}
                    onSuccess={() => {
                        fetchData(); // Refresh report on delete, need to make fetchData available or trigger useEffect
                        window.location.reload(); // Simplest forceful refresh for now or move fetchData out.
                    }}
                />
            )}
        </div>
    );
};

export default ReporteCxP;
